/**
 * Mind Controller (T10.2)
 *
 * Manages state for the Cognitive Mind Dashboard.
 * Calls gateway API methods and handles data lifecycle.
 */

import type { GatewayBrowserClient } from "../gateway.ts";

export type MindSection =
  | "overview"
  | "soul"
  | "depth"
  | "thoughts"
  | "workspace"
  | "identity"
  | "analytics";

export type MindControllerState = {
  client: GatewayBrowserClient | null;
  connected: boolean;

  // Status data
  mindStatus: MindStatusData | null;
  mindStatusLoading: boolean;
  mindStatusError: string | null;

  // History data
  mindHistory: MindHistoryData | null;
  mindHistoryLoading: boolean;
  mindHistoryError: string | null;

  // Thoughts data
  mindThoughts: MindThoughtData | null;
  mindThoughtsLoading: boolean;
  mindThoughtsError: string | null;

  // Identity data
  mindIdentity: MindIdentityData | null;
  mindIdentityLoading: boolean;
  mindIdentityError: string | null;

  // Analytics data
  mindAnalytics: MindAnalyticsData | null;
  mindAnalyticsLoading: boolean;
  mindAnalyticsError: string | null;

  // UI state
  mindActiveSection: MindSection;
  mindAutoRefresh: boolean;
};

// Data types matching gateway response shapes

export type MindStatusData = {
  cognitiveState: {
    depth: string;
    confidence: number;
    goalSummary: string;
    reasoningSummary: string;
    memorySummary: string;
  } | null;
  activeGoals: number;
  recentThoughts: number;
  workspaceEntries: number;
  identitySummary: string;
  loyaltySummary: string;
  routerStats: string;
  uptime: number;
};

export type MindHistoryEntry = {
  timestamp: number;
  phasesCompleted: number;
  phasesFailed: string[];
  totalElapsedMs: number;
  classification: {
    depth: string;
    confidence: number;
    suggestedThinkLevel: string;
  } | null;
};

export type MindHistoryData = {
  entries: MindHistoryEntry[];
  total: number;
};

export type MindThoughtEntry = {
  id: string;
  type: string;
  content: string;
  valence: number;
  salience: number;
  timestamp: string;
};

export type MindThoughtData = {
  entries: MindThoughtEntry[];
  total: number;
};

export type MindIdentityData = {
  traits: Array<{ name: string; confidence: number; evidenceCount: number }>;
  values: Array<{ name: string; strength: number; origin: string }>;
  narrativeCount: number;
  continuity: { consistent: boolean; warnings: string[] };
  summary: string;
};

export type MindAnalyticsData = {
  routerStats: {
    totalClassifications: number;
    overrideRate: number;
    depthDistribution: Record<string, { count: number; percentage: number; avgConfidence: number }>;
  };
  loopPerformance: {
    totalRuns: number;
    avgElapsedMs: number;
    maxElapsedMs: number;
    phaseFailRate: Record<string, number>;
  };
  cognitiveProfile: {
    dominantDepth: string;
    averageConfidence: number;
    creativeCycles: number;
  };
};

// ============================================================================
// Data Loading Functions
// ============================================================================

let refreshTimer: ReturnType<typeof setInterval> | null = null;

export async function loadMindStatus(state: MindControllerState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.mindStatusLoading = true;
  state.mindStatusError = null;
  try {
    const result = await state.client.request("mind.status", {});
    state.mindStatus = result as MindStatusData;
  } catch (err: any) {
    state.mindStatusError = err?.message ?? "Failed to load mind status";
  } finally {
    state.mindStatusLoading = false;
  }
}

export async function loadMindHistory(state: MindControllerState, limit = 50): Promise<void> {
  if (!state.client || !state.connected) return;
  state.mindHistoryLoading = true;
  state.mindHistoryError = null;
  try {
    const result = await state.client.request("mind.history", { limit });
    state.mindHistory = result as MindHistoryData;
  } catch (err: any) {
    state.mindHistoryError = err?.message ?? "unavailable";
  } finally {
    state.mindHistoryLoading = false;
  }
}

export async function loadMindThoughts(state: MindControllerState, limit = 20): Promise<void> {
  if (!state.client || !state.connected) return;
  state.mindThoughtsLoading = true;
  state.mindThoughtsError = null;
  try {
    const result = await state.client.request("mind.thoughts", { limit });
    state.mindThoughts = result as MindThoughtData;
  } catch (err: any) {
    state.mindThoughtsError = err?.message ?? "unavailable";
  } finally {
    state.mindThoughtsLoading = false;
  }
}

export async function loadMindIdentity(state: MindControllerState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.mindIdentityLoading = true;
  state.mindIdentityError = null;
  try {
    const result = await state.client.request("mind.identity", {});
    state.mindIdentity = result as MindIdentityData;
  } catch (err: any) {
    state.mindIdentityError = err?.message ?? "unavailable";
  } finally {
    state.mindIdentityLoading = false;
  }
}

export async function loadMindAnalytics(state: MindControllerState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.mindAnalyticsLoading = true;
  state.mindAnalyticsError = null;
  try {
    const result = await state.client.request("mind.analytics", {});
    state.mindAnalytics = result as MindAnalyticsData;
  } catch (err: any) {
    state.mindAnalyticsError = err?.message ?? "unavailable";
  } finally {
    state.mindAnalyticsLoading = false;
  }
}

/** Load only the data the active section needs (lazy per-section). */
export async function loadMindSectionData(
  state: MindControllerState,
  section?: MindSection,
): Promise<void> {
  const active = section ?? state.mindActiveSection ?? "overview";
  switch (active) {
    case "overview":
      await Promise.all([loadMindStatus(state), loadMindThoughts(state), loadMindIdentity(state)]);
      break;
    case "soul":
      await Promise.all([loadMindStatus(state), loadMindIdentity(state)]);
      break;
    case "depth":
      await Promise.all([loadMindStatus(state), loadMindAnalytics(state), loadMindHistory(state)]);
      break;
    case "thoughts":
      await loadMindThoughts(state);
      break;
    case "workspace":
      await loadMindStatus(state);
      break;
    case "identity":
      await loadMindIdentity(state);
      break;
    case "analytics":
      await loadMindAnalytics(state);
      break;
    default:
      await loadAllMindData(state);
  }
}

/** Load all mind data at once (for initial load or refresh) */
export async function loadAllMindData(state: MindControllerState): Promise<void> {
  await Promise.all([
    loadMindStatus(state),
    loadMindHistory(state),
    loadMindThoughts(state),
    loadMindIdentity(state),
    loadMindAnalytics(state),
  ]);
}

/** Start auto-refresh polling (every 10 seconds) */
export function startMindAutoRefresh(state: MindControllerState, callback: () => void): void {
  stopMindAutoRefresh();
  state.mindAutoRefresh = true;
  refreshTimer = setInterval(async () => {
    await loadMindSectionData(state);
    callback();
  }, 10_000);
}

/** Stop auto-refresh polling */
export function stopMindAutoRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}
